import { type AxiosRequestConfig } from 'axios';
import { useRef } from 'react';
import type { MutateOptions, UseMutationOptions, UseMutationResult } from 'react-query';
import { useMutation } from 'react-query';
import _ from 'scbk-services/utils/lodash-extension.ts';

import { Modules, useConfig } from 'scbk-services/config';
import { useErrorHandler } from 'scbk-services/error';

import { RequestType } from 'scbk-services/http/constants';
import { useHttpContext } from 'scbk-services/http/providers/HttpProvider';
import { RestService } from 'scbk-services/http/services/rest-service.ts';
import { createErrorDetailsFromError } from 'scbk-services/http/utils/rest-response-handler';
import { ErrorDisplayType } from 'scbk-services/types/error/error';
import type {
    CommonRestResponse,
    CustomAxiosRequestConfig,
} from 'scbk-services/types/http/axios-extension';
import type { RestResponse } from 'scbk-services/types/http/rest-response';

/**
 *
 */
export interface UseRestPostOptions extends AxiosRequestConfig {
    baseUrl?: string;
    endpoint: string;
}

/**
 * useRestPost 결과 타입
 */
export type UseRestPostResult<Params, Data, Result = CommonRestResponse<Data>> = {
    request: (
        param: Params,
        options?: UseRestPostMutationOptions<Params, Data, Result>,
    ) => Promise<Data | undefined>;
    requestWithSecure: (
        param: Params,
        options?: UseRestPostMutationOptions<Params, Data, Result>,
    ) => Promise<Data | undefined>;
} & UseMutationResult<Result, CommonRestResponse<Data>, Params>;

/**
 *
 */
export interface UseRestPostMutationOptions<Params, Data, Result = CommonRestResponse<Data>>
    extends UseMutationOptions<Result, RestResponse.ErrorDetails, Params> {
    skipUnauthorizedErrorHandler?: boolean;
    skipForbiddenErrorHandler?: boolean;
    onPoorInternet?: (error?: number) => void;
    errorDisplayType?: ErrorDisplayType;
    forceErrorDisplayPage?: boolean;
    errorOnConfirm?: () => Promise<void> | void;
    useSecureContext?: boolean;
    skipLoading?: boolean;
    /*[2026.04.02] 권태민 추가*/
    errorMenuId?: string; // ASIS : ERROR_AFTER_MOVE_MENU_ID : 에러페이지 이후 이동할 menuId
    errorMenuParameters?: Record<string, unknown>; // ERROR_AFTER_MOVE_PARAMETER : 에러페이지 이후 이동할 페이지에 전달할 parameter
    clearHistory?: boolean;
    /* [2026.04.22] 최성돈 추가 권한체크 관련 옵션 추가 */
    forceCheckCode?: string;
}

/**
 *
 * @param options
 * @param mutationOptions
 * @returns
 */
const useRestPost = <Params, Data, Result = CommonRestResponse<Data>>(
    options: UseRestPostOptions,
    mutationOptions?: UseRestPostMutationOptions<Params, Data>,
): UseRestPostResult<Params, Data, Result> => {
    const { getConfig } = useConfig();
    const { baseUrl, endpoint, ...config } = options;
    const {
        baseUrl: contextBaseUrl,
        onRestDefaultError,
        onRestUnauthorizedError,
        onRestForbiddenHandler,
    } = useHttpContext();

    const targetUrl = _.isEmpty(baseUrl) ? contextBaseUrl : baseUrl;
    const { handleErrorByCode } = useErrorHandler();
    // const { jwtUseMode } = getConfig(Modules.COMMON);

    const requestOptionRef = useRef<UseRestPostMutationOptions<Params, Data> | undefined>({
        skipLoading: mutationOptions?.skipLoading ?? false,
    });

    /**
     * 오류 응답 처리
     * @param res 오류 응답
     * @param variables
     * @param context
     * @returns
     */
    const handleOnError = (res: unknown, variables: Params, context: unknown) => {
        // error display type 설정 : 기본값 MODAL
        const displayType = mutationOptions?.errorDisplayType ?? ErrorDisplayType.MODAL;
        const onConfirm = mutationOptions?.errorOnConfirm;
        const errorMenuId = mutationOptions?.errorMenuId ?? '';
        const errorMenuParameters = mutationOptions?.errorMenuParameters ?? {};
        const clearHistory = mutationOptions?.clearHistory ?? false;

        const onError = mutationOptions?.onError as
            | ((
                  error: RestResponse.ErrorDetails,
                  variables: unknown,
                  context: unknown,
              ) => Promise<unknown> | void)
            | undefined;

        //---------------------------------------------------------------
        //axios + 일반 오류 처리로 통합 함
        //성공 콜백에서 런타임오류가 발생하는경우 onError에서 처리를 해야한다.
        //---------------------------------------------------------------
        if (res instanceof Error) {
            const error = createErrorDetailsFromError(res);
            if (!error) {
                return;
            }

            // if (_.isNotNil(mutationOptions?.onError)) {
            //     mutationOptions?.onError?.(error, variables, context);
            // } else {
            // 특정 error code에 대한 처리
            handleErrorByCode(
                error,
                displayType,
                mutationOptions?.forceErrorDisplayPage ?? false,
                variables,
                context,
                onConfirm,
                errorMenuId,
                errorMenuParameters,
                clearHistory,
                onError,
            );
            // }
            return;
        }

        const response = res as CommonRestResponse<Data>;

        const rawErrorMessage = response?.data?.error?.errorMessage;

        // errorMessage ^ br로 치환
        const errorMessage =
            typeof rawErrorMessage === 'string'
                ? rawErrorMessage.replace(/\^/g, '<br/>').replace('<br>', '<br/>')
                : (rawErrorMessage ?? '');

        const error = {
            ...response?.data?.error,
            errorMessage,
        } as RestResponse.ErrorDetails;

        // when native network layer throw native error
        // will convert native error to a http error with status as 1000
        // and body with negative error code
        if (
            _.isNotNil(response?.response?.status) &&
            _.isEqual(response?.response?.status, 1000) &&
            _.isNotNil(mutationOptions?.onPoorInternet)
        ) {
            mutationOptions?.onPoorInternet?.(response?.response?.data?.code);
            return;
        }

        if (
            (_.isNil(response.status) || _.isEmpty(String(response.status))) &&
            _.isNotNil(mutationOptions?.onPoorInternet)
        ) {
            mutationOptions?.onPoorInternet?.();
            return;
        }

        if (_.inRange(response.status, 200, 300)) {
            if (_.isNotNil(onRestDefaultError)) {
                onRestDefaultError?.(error);
            }

            // if (_.isNotNil(mutationOptions?.onError)) {
            //     mutationOptions?.onError?.(error, variables, context);
            // } else {
            // 특정 error code에 대한 처리
            handleErrorByCode(
                error,
                displayType,
                mutationOptions?.forceErrorDisplayPage ?? false,
                variables,
                context,
                onConfirm,
                errorMenuId,
                errorMenuParameters,
                clearHistory,
                onError,
            );
            // }
            return;
        }

        if (
            mutationOptions?.skipUnauthorizedErrorHandler !== true &&
            _.isEqual(response.status, 401) &&
            _.isNotNil(onRestUnauthorizedError)
        ) {
            onRestUnauthorizedError?.(error);
            return;
        }

        // if (_.isNotNil(mutationOptions?.onError)) {
        //     mutationOptions?.onError?.(error, variables, context);
        //     return;
        // } else {
        // 특정 error code에 대한 처리
        handleErrorByCode(
            error,
            displayType,
            mutationOptions?.forceErrorDisplayPage ?? false,
            variables,
            context,
            onConfirm,
            errorMenuId,
            errorMenuParameters,
            clearHistory,
            onError,
        );
        // }
        return;
    };

    /**
     *
     */
    const mutation = useMutation<Result, CommonRestResponse<Data>, Params>({
        mutationFn: async (data: Params) => {
            const _config = {
                ...config,
                customBaseUrl: targetUrl,
                requestType: RequestType.REST,
                authenticationPromise:
                    mutationOptions?.skipForbiddenErrorHandler !== true
                        ? onRestForbiddenHandler
                        : undefined,
                skipLoading: requestOptionRef.current?.skipLoading,
                //거래이용시간체크 관련 처리
                useForceCheckCode: requestOptionRef.current?.forceCheckCode ? true : false,
                forceCheckCode: requestOptionRef.current?.forceCheckCode,
            } as CustomAxiosRequestConfig;
            const { jwtUseMode } = getConfig(Modules.COMMON);

            const restService = new RestService(targetUrl as string, jwtUseMode);
            const response = await restService.post<CommonRestResponse<Data>>(
                endpoint,
                data,
                _config,
            );

            return response;
        },
        retry: false,
        ...mutationOptions,
        onError: handleOnError,
    } as UseMutationOptions<Result, CommonRestResponse<Data>, Params>);

    /**
     *
     */
    const secureContextMutation = useMutation<Result, CommonRestResponse<Data>, Params>({
        mutationFn: async (data: Params) => {
            const _config = {
                ...config,
                customBaseUrl: targetUrl,
                requestType: RequestType.REST,
                authenticationPromise:
                    mutationOptions?.skipForbiddenErrorHandler !== true
                        ? onRestForbiddenHandler
                        : undefined,
                skipLoading: requestOptionRef.current?.skipLoading,
                // SecureContext 처리여부
                useSecureContext: true,
                //거래이용시간체크 관련 처리
                useForceCheckCode: requestOptionRef.current?.forceCheckCode ? true : false,
                forceCheckCode: requestOptionRef.current?.forceCheckCode,
            } as CustomAxiosRequestConfig;
            const { jwtUseMode } = getConfig(Modules.COMMON);

            const restService = new RestService(targetUrl as string, jwtUseMode);
            const response = await restService.post<CommonRestResponse<Data>>(
                endpoint,
                data,
                _config,
            );

            return response;
        },
        retry: false,
        ...mutationOptions,
        onError: handleOnError,
    } as UseMutationOptions<Result, CommonRestResponse<Data>, Params>);

    /**
     *
     * @param param Params 타입의 요청 데이터
     * @param options
     * @returns
     */
    const request = async (
        param: Params,
        options?: UseRestPostMutationOptions<Params, Data, Result>,
    ): Promise<Data | undefined> => {
        try {
            const _opt: UseRestPostMutationOptions<Params, Data, Result> = {
                ...options,
            };

            const { forceCheckCode = '' } = _opt;
            if (forceCheckCode) {
                requestOptionRef.current = {
                    ...requestOptionRef.current,
                    forceCheckCode,
                };
            }

            const res = (await mutation.mutateAsync(
                param,
                _opt as MutateOptions<Result, CommonRestResponse<Data>, Params>,
            )) as CommonRestResponse<Data>;
            const { data } = res;
            return data.data;
        } finally {
            //requestOptionRef.current = undefined;
        }
    };

    /**
     * <p>
     * 추가인증/보안매체/전자서명/보안키패드용 API 호출 함수
     * API 호출 시 secure context store에 저장된 정보들을 API 호출 시 함께 보낸다.
     * </p>
     * @param param 타입의 요청 데이터
     * @param options
     * @returns
     */
    const requestWithSecure = async (
        param: Params,
        options?: UseRestPostMutationOptions<Params, Data, Result>,
    ): Promise<Data | undefined> => {
        try {
            const _opt: UseRestPostMutationOptions<Params, Data, Result> = {
                ...options,
            };

            const { forceCheckCode = '' } = _opt;

            if (forceCheckCode) {
                requestOptionRef.current = {
                    ...requestOptionRef.current,
                    forceCheckCode,
                };
            }

            console.log('# requestWithSecure options', _opt);

            const res = (await secureContextMutation.mutateAsync(
                param,
                _opt as MutateOptions<Result, CommonRestResponse<Data>, Params>,
            )) as CommonRestResponse<Data>;
            const { data } = res;
            return data.data;
        } finally {
            //requestOptionRef.current = undefined;
        }
    };

    return {
        request,
        requestWithSecure,
        ...mutation,
    };
};

export default useRestPost;
